import { useQuery } from '@apollo/client';
import { isEmpty, isNil } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { Queries } from '@shared/api';
import { RelatedEntity } from '@shared/data/entity/fields/RelatedEntityField';
import { FieldRecord } from '@shared/scenes/entity/types';
import { RelatedCardComponent } from '@shared/scenes/workflows/config';
import { useWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import { API } from '@shared/types/api';
import { notNil } from '@shared/util/notNil';
import { omitTypeName } from '@shared/util/omitTypeName';
import { useFilterByComponents } from './useFilterByComponents';

export function useRelatedEntitySearch(
  relatedCardComponent: RelatedCardComponent,
  fields: FieldRecord,
): {
  keyword: string;
  onSearch: (keyword: string) => void;
  searchResults: RelatedEntity[];
  reset: VoidFunction;
  loading: boolean;
  hasMore: boolean;
  loadMore: VoidFunction;
} {
  const { workflowTemplateId } = relatedCardComponent;
  const [_keyword, setKeyword] = useState<string>('');
  const [keyword] = useDebounce(_keyword, 300);
  const { workflowTemplate, loading: isWorkflowTemplateLoading } =
    useWorkflowTemplate(workflowTemplateId);
  const sortByComponents = useSortByComponents(relatedCardComponent);
  const filterByComponents = useFilterByComponents({
    workflowTemplate,
    fields,
    relatedCardComponent,
  });

  const variables = useMemo(
    () => ({
      input: {
        workflowTemplateId: workflowTemplateId ?? '',
        limit: 50,
        cursor: '',
        sortByComponents,
        filterByComponents,
        omniSearchQuery: !isEmpty(keyword) ? keyword : null,
      },
    }),
    [workflowTemplateId, keyword, sortByComponents, filterByComponents],
  );

  const {
    data: listEntities,
    loading: isEntitiesLoading,
    refetch,
    fetchMore,
  } = useQuery<API.ListWorkflowEntitiesQuery, API.ListWorkflowEntitiesQueryVariables>(
    Queries.workflow.listWorkflowEntities,
    {
      variables,
      skip: isNil(workflowTemplateId) || isWorkflowTemplateLoading,
      fetchPolicy: 'cache-and-network',
    },
  );

  useEffect(() => {
    refetch();
  }, [refetch]);

  const searchResults: RelatedEntity[] = useMemo(() => {
    return listEntities?.list?.entities?.filter(notNil) ?? [];
  }, [listEntities?.list?.entities]);

  const reset = useCallback(() => setKeyword(''), [setKeyword]);

  const loadMore = useCallback(() => {
    if (listEntities?.list?.hasMore && listEntities.list.cursor) {
      fetchMore({
        variables: {
          ...variables,
          input: {
            ...variables.input,
            cursor: listEntities.list.cursor,
          },
        },
      });
    }
  }, [listEntities?.list, fetchMore, variables]);

  return {
    keyword,
    reset,
    onSearch: setKeyword,
    searchResults,
    loading: isWorkflowTemplateLoading || isEntitiesLoading,
    loadMore,
    hasMore: !!listEntities?.list?.hasMore,
  };
}

function useSortByComponents(component: RelatedCardComponent): API.SortByComponent[] {
  return useMemo(() => {
    const defaultSort: API.SortByComponent = {
      componentId: 'updatedAt',
      direction: 'DESC',
    };

    return (
      component.listDefault?.sortByComponents?.filter(notNil).map(omitTypeName) ?? [defaultSort]
    );
  }, [component.listDefault?.sortByComponents]);
}
